import { useCallback, useMemo } from 'react';
import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';
import { CheckCircleIcon } from '@heroicons/react/24/solid';

import { Button as ButtonV2, Heading, Modal, ModalContent } from '@restworld/ui-ds-base';
import { useAuth } from '@restworld/feature-authentication';

import { formatCoveredUntil } from '../utils/covered-until';

interface PutOfflineJobPositionSuccessModalProps {
  isOpen: boolean;
  onClose: () => void;
  // Set when going offline was scheduled at the end of the PPU paid period instead of right away.
  scheduledOfflineAt?: string | null;
}

export function PutOfflineJobPositionSuccessModal({
  isOpen,
  onClose,
  scheduledOfflineAt,
}: PutOfflineJobPositionSuccessModalProps) {
  const { t, i18n } = useTranslation('job-positions-page');
  const language = i18n?.language ?? 'it';
  const router = useRouter();
  const { user } = useAuth();

  const isUsingPPU = user?.using_ppu_business_model === true;

  const scheduledAt = useMemo(
    () => (scheduledOfflineAt ? formatCoveredUntil(scheduledOfflineAt, t, language) : null),
    [scheduledOfflineAt, t, language]
  );

  const handleOpenNewPosition = useCallback(() => {
    onClose();
    router.push('/employer/job-positions/onboarding');
  }, [onClose, router]);

  const handleViewPayments = useCallback(() => {
    onClose();
    router.push('/employer/payments-and-invoices');
  }, [onClose, router]);

  // PPU version - New business model
  if (isUsingPPU) {
    const titleEl = (
      <div className="flex flex-col items-center gap-4">
        <CheckCircleIcon className="w-12 h-12 text-green-600" />
        <Heading color="neutral" align="center" className="leading-[30px] px-4 sm:px-0">
          {scheduledAt ? t('scheduled_offline_success_title') : t('put_offline_success_ppu_title')}
        </Heading>
      </div>
    );

    const footerEl = (
      <div className="flex flex-col justify-center items-center gap-4 w-full">
        <ButtonV2 onClick={onClose} fullwidth variant="primary">
          {t('put_offline_success_ppu_button')}
        </ButtonV2>
      </div>
    );

    return (
      <Modal open={isOpen} onOpenChange={(v) => !v && onClose()}>
        <ModalContent className="w-full max-w-[640px] lg:max-w-screen-sm" title={titleEl} footer={footerEl}>
          <div className="flex flex-col justify-center items-center gap-5 w-full text-center mt-6">
            <p className="text-base font-medium text-neutral-700">
              {scheduledAt
                ? t('scheduled_offline_success_description', scheduledAt)
                : t('put_offline_success_ppu_description')}
            </p>
            <p className="text-base font-medium text-neutral-700">{t('put_offline_success_ppu_reminder')}</p>
          </div>
        </ModalContent>
      </Modal>
    );
  }

  // Legacy version - Old business model with flexible slots

  const titleEl = (
    <div className="flex flex-col items-center gap-4">
      <Heading color="neutral" align="center" className="leading-[30px] px-4 sm:px-0">
        {t('put_offline_success_title')}
      </Heading>
    </div>
  );

  const footerEl = (
    <div className="flex flex-col justify-center items-center gap-4 w-full">
      <ButtonV2 onClick={handleOpenNewPosition} fullwidth variant="primary">
        {t('put_offline_success_new_position_button')}
      </ButtonV2>
      <ButtonV2 variant="tertiary" fullwidth onClick={handleViewPayments}>
        {t('put_offline_success_payments_button')}
      </ButtonV2>
    </div>
  );

  return (
    <Modal open={isOpen} onOpenChange={(v) => !v && onClose()}>
      <ModalContent className="w-full max-w-[640px] lg:max-w-screen-sm" title={titleEl} footer={footerEl}>
        <div className="flex flex-col justify-center items-center gap-5 w-full text-center mt-6">
          <div className="space-y-2">
            <p className="text-base font-medium text-neutral-700">
              {t('put_offline_success_description_part1')}
              <span className="font-bold"> {t('put_offline_success_description_part2')}</span>
            </p>
            <p className="text-base font-medium text-neutral-700">{t('put_offline_success_description_part3')}</p>
          </div>
        </div>
      </ModalContent>
    </Modal>
  );
}

export default PutOfflineJobPositionSuccessModal;
